import {DOCUMENT} from '@angular/common';
import {Inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {BehaviorSubject} from 'rxjs';
import {
  DEFAULT_LANGUAGE_CODE,
  LANGUAGE_COUNTRY_CONFIGS
} from '../config/languages/languages.config';
import {LanguageCountryConfig} from '../config/languages/language-country.config';

@Injectable({providedIn: 'root'})
export class LanguageService {
  private readonly storageKey = 'certikap.language';
  private initialized = false;
  private readonly currentLanguageSubject = new BehaviorSubject<LanguageCountryConfig>(
    this.findLanguage(DEFAULT_LANGUAGE_CODE)
  );

  readonly languages = LANGUAGE_COUNTRY_CONFIGS;
  readonly currentLanguage$ = this.currentLanguageSubject.asObservable();

  constructor(
    private translate: TranslateService,
    @Inject(DOCUMENT) private document: Document
  ) {}

  get currentLanguage(): LanguageCountryConfig {
    return this.currentLanguageSubject.value;
  }

  initialize(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.translate.addLangs(this.languages.map(language => language.code));
    this.translate.setDefaultLang(DEFAULT_LANGUAGE_CODE);

    const storedLanguage = localStorage.getItem(this.storageKey);
    const browserLanguage = navigator.language ? navigator.language.toLowerCase() : '';
    const detectedLanguage = this.languages.find(language =>
      browserLanguage === language.code
      || browserLanguage === language.locale.toLowerCase()
      || browserLanguage.startsWith(language.code + '-')
    );

    this.use(storedLanguage || (detectedLanguage && detectedLanguage.code) || DEFAULT_LANGUAGE_CODE);
  }

  use(code: string): void {
    const language = this.findLanguage(code);
    this.translate.use(language.code);
    localStorage.setItem(this.storageKey, language.code);
    this.document.documentElement.lang = language.locale;
    this.document.documentElement.dir = 'ltr';
    this.currentLanguageSubject.next(language);
  }

  private findLanguage(code: string): LanguageCountryConfig {
    return LANGUAGE_COUNTRY_CONFIGS.find(language => language.code === code)
      || LANGUAGE_COUNTRY_CONFIGS.find(language => language.code === DEFAULT_LANGUAGE_CODE)
      || LANGUAGE_COUNTRY_CONFIGS[0];
  }
}
